import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Sparkle, Copy, Check, Warning, Spinner,
  FilePdf, MagicWand, Code, FloppyDisk, ArrowSquareOut,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../utils/apiUrl';
import { authFetch } from '../utils/authFetch';

const DEFAULT_PDF_URL =
  'https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/9dcgpya8_science.pdf';

const DISCIPLINES = [
  { slug: 'biology', label: 'Biology' },
  { slug: 'chemistry', label: 'Chemistry' },
  { slug: 'physics', label: 'Physics' },
  { slug: 'combined', label: 'Combined' },
];

const DIFFICULTIES = ['foundation', 'higher'];

export default function AdminContentPipeline() {
  const { user, loading } = useAuth();

  const [form, setForm] = useState({
    pdfUrl: DEFAULT_PDF_URL,
    pageStart: 20,
    pageEnd: 22,
    discipline: 'biology',
    sectionSlug: 'humans-as-organisms',
    topicSlug: 'nutrition',
    topicTitle: 'Nutrition',
    topicId: 'bio-2.1',
    difficulty: 'foundation',
    pageRef: 'Section 2 · Topic 1',
    diagram: '',
  });

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setCopied(false);
    setSaveResult(null);
    setSaveError(null);
  }, [result]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Spinner size={40} className="animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/admin/content-pipeline/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_url: form.pdfUrl,
          page_start: Number(form.pageStart),
          page_end: Number(form.pageEnd),
          discipline_slug: form.discipline,
          section_slug: form.sectionSlug,
          topic_slug: form.topicSlug,
          topic_title: form.topicTitle,
          topic_id: form.topicId || null,
          difficulty: form.difficulty,
          page_ref: form.pageRef || null,
          diagram: form.diagram || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyJs = async () => {
    if (!result?.js_export) return;
    await navigator.clipboard.writeText(result.js_export);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const saveToSectionFile = async () => {
    if (!result?.topic_draft) return;
    setSaving(true);
    setSaveError(null);
    setSaveResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/admin/content-pipeline/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discipline_slug: form.discipline,
          section_slug: form.sectionSlug,
          topic: result.topic_draft,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.detail || data.message || `Save failed (${res.status})`);
      }
      setSaveResult(data);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const previewHref =
    result?.topic_draft && form.discipline && form.sectionSlug && result.topic_draft.slug
      ? `/science/${form.discipline}/${form.sectionSlug}/${result.topic_draft.slug}`
      : null;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="admin-back">
          <ArrowLeft size={16} /> Back to Admin
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="neo-card bg-gradient-to-br from-indigo-600 to-violet-600 text-white mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl border-2 border-white/30 flex items-center justify-center flex-shrink-0">
              <MagicWand size={24} weight="fill" />
            </div>
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider opacity-80 mb-1">Admin Tools</div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-1">PDF → Rich Content Pipeline</h1>
              <p className="opacity-90 text-sm">Ingest a chapter range, drive Gemini 2.5 Pro over the text + diagrams, and receive a reviewable JSON draft for one topic.</p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="neo-card space-y-4" data-testid="pipeline-form">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2"><FilePdf size={18} weight="fill" className="text-primary" /> Source & target</h2>

            <div>
              <label className="block text-xs font-bold mb-1">PDF URL</label>
              <input
                type="url"
                value={form.pdfUrl}
                onChange={(e) => update('pdfUrl', e.target.value)}
                className="neo-input w-full text-xs font-mono"
                data-testid="pipeline-pdf-url"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1">Page start (1-indexed)</label>
                <input type="number" min="1" value={form.pageStart} onChange={(e) => update('pageStart', e.target.value)} className="neo-input w-full" data-testid="pipeline-page-start" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Page end (inclusive)</label>
                <input type="number" min="1" value={form.pageEnd} onChange={(e) => update('pageEnd', e.target.value)} className="neo-input w-full" data-testid="pipeline-page-end" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1">Discipline</label>
                <select value={form.discipline} onChange={(e) => update('discipline', e.target.value)} className="neo-input w-full" data-testid="pipeline-discipline">
                  {DISCIPLINES.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Difficulty</label>
                <select value={form.difficulty} onChange={(e) => update('difficulty', e.target.value)} className="neo-input w-full" data-testid="pipeline-difficulty">
                  {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold mb-1">Section slug</label>
              <input type="text" value={form.sectionSlug} onChange={(e) => update('sectionSlug', e.target.value)} className="neo-input w-full" data-testid="pipeline-section-slug" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1">Topic slug</label>
                <input type="text" value={form.topicSlug} onChange={(e) => update('topicSlug', e.target.value)} className="neo-input w-full" data-testid="pipeline-topic-slug" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Topic ID</label>
                <input type="text" value={form.topicId} onChange={(e) => update('topicId', e.target.value)} className="neo-input w-full" placeholder="e.g. bio-2.1" data-testid="pipeline-topic-id" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold mb-1">Topic title</label>
              <input type="text" value={form.topicTitle} onChange={(e) => update('topicTitle', e.target.value)} className="neo-input w-full" data-testid="pipeline-topic-title" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1">Page reference (display)</label>
                <input type="text" value={form.pageRef} onChange={(e) => update('pageRef', e.target.value)} className="neo-input w-full" placeholder="Section 2 · Topic 1" data-testid="pipeline-page-ref" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Diagram id (optional)</label>
                <input type="text" value={form.diagram} onChange={(e) => update('diagram', e.target.value)} className="neo-input w-full" placeholder="e.g. digestive-system" data-testid="pipeline-diagram" />
              </div>
            </div>

            <button
              onClick={run}
              disabled={busy}
              className="neo-btn-primary w-full py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="pipeline-generate"
            >
              {busy ? <><Spinner size={18} className="animate-spin" /> Running pipeline…</> : <><Sparkle size={18} weight="fill" /> Generate draft</>}
            </button>
            {busy && (
              <p className="text-xs text-muted-foreground">
                Typically takes 30–60 seconds for a 2-3 page slice. Larger ranges take longer.
              </p>
            )}
          </div>

          {/* Result */}
          <div className="space-y-4">
            {error && (
              <div className="neo-card bg-rose-50 dark:bg-rose-900/20 border-rose-300" data-testid="pipeline-error">
                <div className="flex items-start gap-2">
                  <Warning size={20} weight="fill" className="text-rose-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-sm text-rose-700 dark:text-rose-300">Pipeline failed</h3>
                    <p className="text-xs text-rose-600 dark:text-rose-300 mt-1 whitespace-pre-wrap">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!result && !error && !busy && (
              <div className="neo-card text-center p-8 opacity-70">
                <MagicWand size={36} weight="fill" className="text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Fill in the form and click <strong>Generate draft</strong>. The result appears here.</p>
              </div>
            )}

            {result?.parse_error && (
              <div className="neo-card bg-amber-50 dark:bg-amber-900/20 border-amber-300" data-testid="pipeline-parse-error">
                <div className="flex items-start gap-2">
                  <Warning size={18} weight="fill" className="text-amber-500 flex-shrink-0" />
                  <p className="text-xs"><strong>JSON parse warning:</strong> {result.parse_error}. See raw response below.</p>
                </div>
              </div>
            )}

            {result?.topic_draft && (
              <div className="neo-card" data-testid="pipeline-draft">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-emerald-500" /> Draft ready
                  </h2>
                  <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                    {result.model} · pages {result.page_range[0]}–{result.page_range[1]}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-bold text-center mb-4">
                  <div className="p-2 rounded bg-secondary"><span className="block text-lg">{(result.topic_draft.learn?.chunks || []).length}</span>chunks</div>
                  <div className="p-2 rounded bg-secondary"><span className="block text-lg">{(result.topic_draft.glossary || []).length}</span>terms</div>
                  <div className="p-2 rounded bg-secondary"><span className="block text-lg">{
                    (result.topic_draft.practice?.warmUp || []).length +
                    (result.topic_draft.practice?.level1 || []).length +
                    (result.topic_draft.practice?.level2 || []).length +
                    (result.topic_draft.practice?.level3 || []).length
                  }</span>qs</div>
                </div>
                <h3 className="font-bold">{result.topic_draft.title}</h3>
                <p className="text-xs text-muted-foreground mb-3">{result.topic_draft.slug} · {result.topic_draft.difficulty}</p>

                {/* Save / preview actions */}
                <div className="flex flex-wrap gap-2 mb-2">
                  <button
                    onClick={saveToSectionFile}
                    disabled={saving || saveResult?.ok}
                    className="neo-btn-primary !py-2 !px-3 text-xs flex items-center gap-1 disabled:opacity-60"
                    data-testid="pipeline-save"
                  >
                    {saving ? <><Spinner size={14} className="animate-spin" /> Saving…</>
                      : saveResult?.ok ? <><Check size={14} weight="bold" /> Saved — live now</>
                      : <><FloppyDisk size={14} weight="bold" /> Save to section file</>}
                  </button>
                  {saveResult?.ok && previewHref && (
                    <a
                      href={previewHref}
                      target="_blank"
                      rel="noreferrer"
                      className="neo-btn-secondary !py-2 !px-3 text-xs flex items-center gap-1"
                      data-testid="pipeline-preview"
                    >
                      <ArrowSquareOut size={14} weight="bold" /> Preview topic
                    </a>
                  )}
                </div>
                {saveResult?.ok && (
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mb-2" data-testid="pipeline-save-success">
                    {saveResult.action === 'upserted' ? 'Replaced existing topic.' : 'Added to section.'} Section now has {saveResult.total_in_section} authored topic(s).
                  </p>
                )}
                {saveError && (
                  <div className="flex items-start gap-2 p-2 rounded bg-rose-50 dark:bg-rose-900/20 text-xs mb-2" data-testid="pipeline-save-error">
                    <Warning size={14} weight="fill" className="text-rose-500 flex-shrink-0 mt-0.5" />
                    <span className="text-rose-700 dark:text-rose-300">{saveError}</span>
                  </div>
                )}

                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-bold text-muted-foreground">Preview chunks</summary>
                  <div className="mt-2 space-y-2">
                    {(result.topic_draft.learn?.chunks || []).map((c, i) => (
                      <div key={i} className="p-2 rounded bg-secondary text-xs">
                        <div className="font-bold">{c.heading}</div>
                        <div className="line-clamp-3 opacity-80">{c.body}</div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

            {result?.js_export && (
              <div className="neo-card" data-testid="pipeline-js">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold flex items-center gap-1"><Code size={16} weight="bold" /> JS object</h3>
                  <button
                    onClick={copyJs}
                    className="neo-btn-secondary !py-1 !px-3 text-xs flex items-center gap-1"
                    data-testid="pipeline-copy-js"
                  >
                    {copied ? <><Check size={14} weight="bold" /> Copied</> : <><Copy size={14} weight="bold" /> Copy</>}
                  </button>
                </div>
                <pre className="bg-slate-900 text-slate-100 rounded p-3 text-[10px] leading-snug overflow-auto max-h-96 font-mono">
{result.js_export}
                </pre>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Paste into the matching section file in <code className="bg-secondary px-1 rounded">/app/frontend/src/data/</code>. Review carefully — LLM output should always be sanity-checked before shipping to pupils.
                </p>
              </div>
            )}

            {result?.raw_text_preview && (
              <details className="neo-card" data-testid="pipeline-raw-text">
                <summary className="cursor-pointer font-bold text-sm">Extracted text ({result.raw_text_preview.length} chars)</summary>
                <pre className="mt-2 text-xs whitespace-pre-wrap opacity-80 max-h-64 overflow-auto">{result.raw_text_preview}</pre>
              </details>
            )}

            {result?.raw_response && !result?.topic_draft && (
              <details className="neo-card" open>
                <summary className="cursor-pointer font-bold text-sm">Raw LLM response</summary>
                <pre className="mt-2 text-xs whitespace-pre-wrap max-h-96 overflow-auto">{result.raw_response}</pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
